namespace SaFormUtils {
    export const IsValidClassName = "is-valid";
    export const IsInvalidClassName = "is-invalid";
    export const InvalidFeedbackClassName = "invalid-feedback";
    export const WasValidatedClassName = "was-validated";

    /**
     * Validates a form field using HTML5 validation and applies appropriate classes and feedback display based on validity. Only validates visible fields that are input, select, or textarea elements (ones that have checkValidity()).
     * @param field
     * @returns whether that field is valid or not. Note that hidden fields are not validated and will return true, since they are valid enough for not being visible.
     */
    export function validateField(field: Element | null | undefined): boolean {
        if (!(
            field instanceof HTMLInputElement ||
            field instanceof HTMLSelectElement ||
            field instanceof HTMLTextAreaElement
        )) {
            console.error(
                "SaFormUtils Field validation attempted on unsupported element type:",
                field,
            );
            return false;
        }

        if (field.offsetParent !== null) {
            // Basically means is visible, since checkValidity doesn't care about visibility but we don't want to validate hidden fields
            if (field.checkValidity()) {
                field.classList.remove(SaFormUtils.IsInvalidClassName);
                field.classList.add(SaFormUtils.IsValidClassName);
                $(field)
                    .parent()
                    .find(`.${SaFormUtils.InvalidFeedbackClassName}`)
                    ?.hide();
                return true;
            } else {
                field.classList.remove(SaFormUtils.IsValidClassName);
                field.classList.add(SaFormUtils.IsInvalidClassName);
                $(field)
                    .parent()
                    .find(`.${SaFormUtils.InvalidFeedbackClassName}`)
                    ?.show();
                return false;
            }
        }
        return true; // Don't validate hidden fields, just return true since they are valid enough for not being visible
    }

    export function validateJQueryField(field: JQuery<HTMLElement>): boolean {
        if (field.length === 0) {
            console.error(
                "SaFormUtils Field validation attempted on empty jQuery object:",
                field,
            );
            return false;
        }

        const domField = field[0];
        if (!(
            domField instanceof HTMLInputElement ||
            domField instanceof HTMLSelectElement ||
            domField instanceof HTMLTextAreaElement
        )) {
            console.error(
                "SaFormUtils Field validation attempted on unsupported element type:",
                domField,
            );
            return false;
        }

        return validateField(domField);
    }

    /* Validate Form Data */
    export function formIsValid(
        formId: string,
        onAllValid?: (formId: string) => void,
        onAnyInvalid?: (formId: string) => void,
    ): boolean {
        const allFields = $("#" + formId).find(
            "input[type=text], select, textarea, input[type=date], input[type=number], input[type=email], input[type=password], input[type=tel], input[type=url]",
        );
        const invalidFieldIds = new Array<string>();
        $(allFields).each(function () {
            const isValid = validateField(this);
            if (!isValid) {
                const fieldId = $(this).prop("id");
                invalidFieldIds.push(fieldId);
            }
        });
        if (invalidFieldIds.length === 0) {
            if (onAllValid) {
                onAllValid(formId);
            }
            return true;
        } else {
            if (onAnyInvalid) {
                onAnyInvalid(formId);
            }
            return false;
        }
    }

    /**
     * Looks for the first element with the 'is-invalid' class and scrolls to it. If the element is visible and not disabled, it also focuses on it. This is typically used after form validation to bring the user's attention to the first error that needs to be addressed.
     * @returns Whether it found an error to scroll to.
     */
    export function scrollToFirstError(): boolean {
        let foundAnError = false;
        const firstInvalid = $(`.${SaFormUtils.IsInvalidClassName}`).first();

        if (firstInvalid.length) {
            const topOffset = firstInvalid.offset()?.top;
            if (topOffset === undefined || topOffset === null) {
                // Zero is OK here, but is falsy
                console.log(
                    "First invalid element found but has no offset: ",
                    firstInvalid.offset(),
                );
            } else {
                $("html, body").animate(
                    {
                        scrollTop: Math.max(topOffset - 100, 0),
                    },
                    300,
                    function () {
                        if (
                            firstInvalid.is(":visible") &&
                            !firstInvalid.is(":disabled")
                        ) {
                            firstInvalid.trigger("focus");
                        }
                    },
                );

                foundAnError = true; // Don't place this inside the animate, will never return true since it doesn't assign in time for the return
            }
        }

        return foundAnError;
    }
}

globalThis.SaFormUtils = SaFormUtils;
